/**
 * Route guard: auth plus interface_elements visibility (single or multi-resource).
 */

import * as React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';

/** Props for {@link ProtectedRoute}. */
interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * `resource` may be a single interface name or an array.
   * Access is granted if at least one interface has visible elements
   * (shared remote / in-person routes).
   */
  requiredPermission?: { resource: string | string[]; action?: string };
  requireAuth?: boolean;
  fallback?: React.ReactNode;
  redirectTo?: string;
}

/** Load one interface; needed because hooks cannot be called conditionally. */
const SingleInterfaceGuard: React.FC<{
  interfaceName: string;
  onResult: (hasElements: boolean, isLoading: boolean) => void;
}> = ({ interfaceName, onResult }) => {
  const { elements, isLoading } = useInterfaceElements(interfaceName);
  React.useEffect(() => {
    onResult(elements.length > 0, isLoading);
  });
  return null;
};

/**
 * Guards routes by checking isAuthenticated and loading interface_elements.
 *
 * The legacy permissions system has been removed. Access is now controlled exclusively
 * through the interface_elements table. This component loads the required interface
 * and grants access if the user has at least one element for that interface.
 *
 * When resource is an array, access is granted if ANY of the interfaces has visible elements.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  requireAuth = true,
  fallback,
  redirectTo = '/login',
}) => {
  const { isAuthenticated } = useAuth();

  const rawResource = requiredPermission?.resource;
  let resources: string[] = [];
  if (rawResource) {
    resources = Array.isArray(rawResource) ? rawResource : [rawResource];
  }

  const [results, setResults] = React.useState<Record<string, { hasElements: boolean; isLoading: boolean }>>({});

  /** Merge one interface's visibility result without rewriting unchanged entries. */
  const updateResult = React.useCallback((name: string, hasElements: boolean, isLoading: boolean) => {
    setResults((prev) => {
      const existing = prev[name];
      if (existing?.hasElements === hasElements && existing?.isLoading === isLoading) return prev;
      return { ...prev, [name]: { hasElements, isLoading } };
    });
  }, []);

  if (requireAuth && !isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  if (!requiredPermission || resources.length === 0) {
    return <>{children}</>;
  }

  const anyLoading = resources.some((r) => results[r]?.isLoading !== false);
  const anyAccess = resources.some((r) => results[r]?.hasElements === true);
  const allResolved = resources.every((r) => r in results && results[r].isLoading === false);

  return (
    <>
      {resources.map((name) => (
        <SingleInterfaceGuard
          key={name}
          interfaceName={name}
          onResult={(hasElements, isLoading) => updateResult(name, hasElements, isLoading)}
        />
      ))}

      {anyLoading && !allResolved && (
        <div className="flex items-center justify-center min-h-full">Chargement...</div>
      )}

      {allResolved && anyAccess && <>{children}</>}
      {allResolved && !anyAccess && (fallback ? <>{fallback}</> : <Navigate to="/unauthorized" replace />)}
    </>
  );
};
